// Browser side of the i18n layer (docs/I18N.md).
// - Public marketing pages take their language from the URL only (/en/… is English),
//   so every page has one crawlable language and hreflang alternates.
// - The teacher app (app.classmori.com, auth pages) follows ?lang=, then the shared
//   cookie, then localStorage, then the browser language.
// The page reloads on a language change, so `locale` is a constant for the page's life.
import {
  DEFAULT_LOCALE, FULL_LOCALES, LOCALE_CONFIG, LOCALE_COOKIE, LOCALE_QUERY, LOCALES, isLightLocale, siteLocale,
  localeFromLanguages, localeFromPath, localizePath, normalizeLocale, readLocaleCookie, type Locale,
} from "../../src/i18n/locales";
import { createTranslator, type MessageParams, type Messages, type Translator } from "../../src/i18n/translate";
import * as format from "../../src/i18n/format";
import { pathExistsIn } from "../../src/traditional-only";

export { LOCALES, FULL_LOCALES, LOCALE_CONFIG, DEFAULT_LOCALE, isLightLocale, type Locale };

// Browser globals, typed locally: Worker tests import web modules through the Worker
// tsconfig (no DOM lib), and this module must also load under Node without a window.
type BrowserGlobals = {
  location?: { pathname: string; search: string; hash: string; href: string; hostname: string; protocol: string; assign(url: string): void; replace(url: string): void };
  document?: { cookie: string; documentElement: { lang: string }; getElementById?(id: string): { textContent: string | null } | null };
  navigator?: { languages?: readonly string[]; language: string };
  localStorage?: { getItem(key: string): string | null; setItem(key: string, value: string): void };
};
const browser = globalThis as unknown as BrowserGlobals;

const STORAGE_KEY = "classmori-ui-locale";
// app.localhost lets local testing reach the teacher app on its own host, like production.
const APP_HOSTS = ["app.classmori.com", "app.localhost"];

/** Base paths of the public site (after removing a /en prefix). Mirrors isPublicPagePath in src/index.ts. */
export function isPublicSitePath(path: string) {
  return ["/", "/about", "/pricing", "/tour", "/privacy", "/terms", "/contact", "/updates"].includes(path)
    || /^\/(help|features|articles|tools)(\/|$)/.test(path);
}

function storedLocale(): Locale | null {
  try { return normalizeLocale(browser.localStorage!.getItem(STORAGE_KEY)); } catch { return null; }
}

const zoneOfBrowser = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return null; } };

function detect(): { locale: Locale; basePath: string; prefixed: boolean } {
  if (!browser.location) return { locale: DEFAULT_LOCALE, basePath: "/", prefixed: false };
  const fromPath = localeFromPath(browser.location!.pathname);
  if (fromPath.prefixed) return fromPath;
  const onAppHost = APP_HOSTS.includes(browser.location!.hostname);
  // Unprefixed public pages are the Chinese site; only the app follows preferences.
  if (!onAppHost && isPublicSitePath(fromPath.basePath)) return fromPath;
  const query = normalizeLocale(new URLSearchParams(browser.location!.search).get(LOCALE_QUERY));
  // The teacher app never runs in a light language (D-231); a saved "it", "th", "uk" or "nl" from the tool pages means the English app.
  const preferred = (query && siteLocale(query))
    ?? (() => { const saved = readLocaleCookie(!browser.document ? null : browser.document!.cookie); return saved && siteLocale(saved); })()
    ?? (() => { const saved = storedLocale(); return saved && siteLocale(saved); })()
    ?? (!browser.navigator ? null : localeFromLanguages(browser.navigator!.languages?.length ? browser.navigator!.languages : [browser.navigator!.language], zoneOfBrowser()))
    ?? DEFAULT_LOCALE;
  return { locale: preferred, basePath: fromPath.basePath, prefixed: false };
}

const detected = detect();
/** Language of this page. */
export const locale: Locale = detected.locale;
/** Current path without the locale prefix: route on this, link with lp(). */
export const basePath = detected.basePath;
export const localeConfig = LOCALE_CONFIG[locale];
export const isDefaultLocale = locale === DEFAULT_LOCALE;

let translator: Translator = createTranslator(locale, null);
let ready = isDefaultLocale;

/** Loads the dictionary for a non-default locale; call (and await) before rendering. */
export async function loadMessages(): Promise<void> {
  if (ready) return;
  const loaders: Partial<Record<Locale, () => Promise<{ default: Messages }>>> = {
    "pt-BR": () => import("../../src/i18n/messages/pt-BR"),
    "tr": () => import("../../src/i18n/messages/tr"),
    "vi": () => import("../../src/i18n/messages/vi"),
    "id": () => import("../../src/i18n/messages/id"),
    "it": () => import("../../src/i18n/messages/it"),
    "th": () => import("../../src/i18n/messages/th"),
    "uk": () => import("../../src/i18n/messages/uk"),
    "nl": () => import("../../src/i18n/messages/nl"),
    "zh-Hans": () => import("../../src/i18n/messages/zh-Hans"),
    en: () => import("../../src/i18n/messages/en"),
    es: () => import("../../src/i18n/messages/es"),
    fr: () => import("../../src/i18n/messages/fr"),
  };
  const load = loaders[locale];
  if (!load) { ready = true; return; }
  const messages = (await load()).default;
  translator = createTranslator(locale, messages);
  ready = true;
}

const warned = new Set<string>();
function guard(source: string) {
  if (ready || warned.has(source)) return;
  warned.add(source);
  // A t() call ran at module load before messages arrived; move it into a function.
  console.error(`[i18n] t() before messages loaded: ${source}`);
}

/** Translate UI text. The key is the exact Traditional Chinese source text. */
export function t(source: string, params?: MessageParams): string {
  guard(source);
  return translator.t(source, params);
}
/** Translate with a disambiguating context (same Chinese, different meaning). */
export function tx(context: string, source: string, params?: MessageParams): string {
  guard(source);
  return translator.tx(context, source, params);
}

/** Pet level badge: "Lv.10" (Chinese, English), "Nv. 10" (Spanish). */
export const levelLabel = (level: number | string) => t("Lv.{level}", { level });

/**
 * Languages an /articles page is public in, as the server listed them in the page (src/articles/render.ts pageData,
 * 教學專欄 D-270): an article exists only in the languages it was translated into, and only after its publish time.
 */
function servedArticleLocales(): readonly string[] | null {
  try { return JSON.parse(browser.document?.getElementById?.("cm-article-meta")?.textContent ?? "null")?.locales ?? null; } catch { return null; }
}
const existsIn = (path: string, target: Locale) => /^\/articles(\/|$)/.test(path) && path === currentBasePath()
  ? (servedArticleLocales()?.includes(target) ?? target === locale)
  : pathExistsIn(path, target);

/** Localized public path: lp("/pricing") → "/en/pricing" on the English site. */
export const lp = (path: string) => localizePath(path, isLightLocale(locale) && !pathExistsIn(path.split(/[?#]/)[0], locale) ? "en" : locale);
/** Base path of the address bar right now (help center articles change it with pushState). */
const currentBasePath = () => browser.location ? localeFromPath(browser.location.pathname).basePath : basePath;
/** Same page in another language (for the language switcher and hreflang-like links); a page that language does not have
 *  (注音符號表 exists only in Traditional Chinese) leads to that language's free tools instead. */
export const pathInLocale = (target: Locale) => existsIn(currentBasePath(), target)
  ? localizePath(currentBasePath(), target) + (!browser.location ? "" : browser.location!.search + browser.location!.hash)
  // An article not translated into that language leads to its 教學專欄 (every full language has one), else its free tools.
  : localizePath(/^\/articles(\/|$)/.test(currentBasePath()) && !isLightLocale(target) ? "/articles" : "/tools", target);
/** Whether the current page has a version in `target` (the language suggestion is only offered then). */
export const pageExistsIn = (target: Locale) => existsIn(currentBasePath(), target);

/** Locale the visitor explicitly chose before (switcher, ?lang=, /en pages), if any. */
export function savedLocale(): Locale | null {
  return readLocaleCookie(browser.document?.cookie ?? null) ?? storedLocale();
}

export const formatNumber = (value: number, options?: Intl.NumberFormatOptions) => format.formatNumber(locale, value, options);
export const formatPrice = (amount: number) => format.formatPrice(locale, amount);
export const formatDate = (value: Date | string | number, options?: Intl.DateTimeFormatOptions) => format.formatDate(locale, value, options);
export const formatDateTime = (value: Date | string | number, options?: Intl.DateTimeFormatOptions) => format.formatDateTime(locale, value, options);
export const formatList = (items: string[], type?: "conjunction" | "disjunction") => format.formatList(locale, items, type);
/** Intl tag for APIs that take a locale directly (toLocaleString, Intl.Collator…). */
export const intlLocale = localeConfig.intl;
export const siteName = localeConfig.siteName;

/** Browser time zone for new classes; falls back to the locale default. */
export function browserTimezone(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || localeConfig.defaultTimezone; } catch { return localeConfig.defaultTimezone; }
}

/** Remember a language for the app and both hosts (cookie is shared across classmori.com subdomains). */
export function rememberLocale(next: Locale) {
  try { browser.localStorage!.setItem(STORAGE_KEY, next); } catch { /* private mode */ }
  if (!browser.document) return;
  const domain = /(^|\.)classmori\.com$/.test(browser.location!.hostname) ? "; domain=classmori.com" : "";
  browser.document!.cookie = `${LOCALE_COOKIE}=${next}; path=/; max-age=31536000; samesite=lax${domain}${browser.location!.protocol === "https:" ? "; secure" : ""}`;
}

/** Switch language: public pages move to the prefixed URL, the app reloads in place. */
export function switchLocale(next: Locale) {
  rememberLocale(next);
  const base = currentBasePath();
  const onPublic = !APP_HOSTS.includes(browser.location!.hostname) && isPublicSitePath(base);
  if (onPublic) { browser.location!.assign(pathInLocale(next)); return; }
  // App pages have no locale prefix: /en/login → /login?lang=zh-Hant.
  const url = new URL(browser.location!.href);
  url.pathname = base;
  url.searchParams.set(LOCALE_QUERY, next);
  browser.location!.replace(url.toString());
}

// English visitors on /en pages carry the choice to app.classmori.com (register/login).
if (browser.document && browser.location) {
  browser.document!.documentElement.lang = localeConfig.htmlLang;
  // Remember only real choices: an /en page, or ?lang= on an app page (public pages ignore ?lang=).
  const queried = normalizeLocale(new URLSearchParams(browser.location!.search).get(LOCALE_QUERY));
  const publicPage = !APP_HOSTS.includes(browser.location!.hostname) && isPublicSitePath(basePath);
  if (detected.prefixed) rememberLocale(locale);
  else if (queried && !publicPage) rememberLocale(queried);
}
